import React, { useEffect, useState } from "react";
import { Form, Input, Button, message, Card, Spin, Switch, Select, Collapse } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpen, faFloppyDisk, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import appTabConfigApi from "../../../api/appTabConfigApi";
import { GUIDE_SCREEN_OPTIONS, OPEN_MODE_OPTIONS } from "./app-config-options";

// Cấu hình NÚT HƯỚNG DẪN ở khối "Mua sắm hoàn tiền" trang chủ app.
// Trước đây nút này viết cứng trong app (mở bài #23) nên đổi đích phải build lại;
// nay ghi vào /img/app-tab-config.json chung với nút giữa thanh tab → app nhận ở
// lần mở sau, KHÔNG cần build/OTA.
const LABEL_MAX = 60;

function HomeGuideConfigForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const label = Form.useWatch("label", form);
    const enabled = Form.useWatch("enabled", form);
    const openMode = Form.useWatch("openMode", form);
    const screenName = Form.useWatch("screenName", form);
    const isScreenMode = openMode === "screen";
    const isPostScreen = isScreenMode && screenName === "PostDetail";

    useEffect(() => {
        (async () => {
            try {
                const res = await appTabConfigApi.getConfig();
                const c = res.data?.data?.homeGuide || {};
                form.setFieldsValue({
                    enabled: c.enabled !== false,
                    label: c.label || "",
                    openMode: c.openMode || "screen",
                    screenName: c.screenName || "PostDetail",
                    postId: c.postId || "",
                    link: c.link || "",
                    injectUserId: c.injectUserId === true,
                });
            } catch {
                message.error("Không tải được cấu hình nút hướng dẫn");
            } finally {
                setLoading(false);
            }
        })();
    }, [form]);

    const onFinish = async (values) => {
        setSaving(true);
        try {
            // Chỉ gửi section homeGuide — backend merge, không đụng nút giữa thanh tab.
            await appTabConfigApi.updateConfig({ homeGuide: values });
            message.success("Đã lưu. App sẽ nhận ở lần mở tiếp theo.");
        } catch (err) {
            message.error(err?.response?.data?.message || "Lưu thất bại");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 text-center">
                <Spin />
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <div className="flex flex-wrap gap-6">
                <div className="flex-1 min-w-[320px]">
                    <div className="flex gap-4 flex-wrap">
                        <Form.Item label="Nhãn nút" name="label" className="flex-1 min-w-[260px]"
                            extra={`Tối đa ${LABEL_MAX} ký tự — dùng được emoji`}>
                            <Input maxLength={LABEL_MAX} showCount placeholder="📖 Hướng dẫn mua sắm hoàn tiền cho người mới" />
                        </Form.Item>
                        <Form.Item label="Hiện nút" name="enabled" valuePropName="checked" className="w-28">
                            <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
                        </Form.Item>
                    </div>

                    <Form.Item label="Cách mở" name="openMode"
                        extra="Giống Tiện ích App: mở màn hình nội bộ hoặc mở link">
                        <Select options={OPEN_MODE_OPTIONS} style={{ maxWidth: 280 }} />
                    </Form.Item>

                    {isScreenMode ? (
                        <>
                            <Form.Item label="Màn hình app" name="screenName"
                                extra="Mở thẳng màn hình trong app khi nhấn nút.">
                                <Select options={GUIDE_SCREEN_OPTIONS} style={{ maxWidth: 320 }} />
                            </Form.Item>
                            {isPostScreen && (
                                <Form.Item label="ID bài viết" name="postId"
                                    rules={[{ pattern: /^\d+$/, message: "Chỉ nhập số" }]}
                                    extra="Bỏ trống → dùng bài mặc định (#23).">
                                    <Input placeholder="23" style={{ maxWidth: 160 }} />
                                </Form.Item>
                            )}
                        </>
                    ) : (
                        <>
                            <Form.Item label="Link" name="link"
                                rules={[{ required: true, message: "Nhập link http/https" }]}
                                extra="Chèn {userId} để gắn user hiện tại. VD: https://longhousee.com/trang/huong-dan-hoan-tien">
                                <Input placeholder="https://..." />
                            </Form.Item>
                            <Form.Item label="Gắn user_id vào link" name="injectUserId" valuePropName="checked">
                                <Switch checkedChildren="Có" unCheckedChildren="Không" />
                            </Form.Item>
                        </>
                    )}

                    <Button type="primary" htmlType="submit" loading={saving}
                        icon={<FontAwesomeIcon icon={faFloppyDisk} />}>
                        Lưu nút hướng dẫn
                    </Button>
                </div>

                {/* Xem trước — mô phỏng dải nút trong khối "Mua sắm hoàn tiền" */}
                <div>
                    <div className="mb-2 text-xs text-content-3">Xem trước</div>
                    {enabled === false ? (
                        <div className="rounded-xl border border-dashed border-line-strong px-6 py-4 text-xs text-content-3">
                            Nút đang ẩn
                        </div>
                    ) : (
                        <div className="inline-flex w-[300px] items-center justify-between rounded-xl border border-line bg-[#FFF7F5] px-3 py-2.5">
                            <span className="text-[12px] font-semibold text-[#EE4D2D]">
                                {(label || "📖 Hướng dẫn mua sắm hoàn tiền cho người mới").slice(0, LABEL_MAX)}
                            </span>
                            <FontAwesomeIcon icon={faChevronRight} className="text-[#EE4D2D]" size="xs" />
                        </div>
                    )}
                </div>
            </div>
        </Form>
    );
}

/** Card gập — để trang Tiện ích App vẫn tập trung vào bảng danh sách. */
export default function HomeGuideConfigCard() {
    return (
        <Card size="small" className="mb-5" styles={{ body: { padding: 0 } }}>
            <Collapse
                ghost
                items={[
                    {
                        key: "home-guide",
                        label: (
                            <span className="font-semibold">
                                <FontAwesomeIcon icon={faBookOpen} className="text-orange-500 mr-2" />
                                Nút hướng dẫn trang chủ App (nhãn, đích đến)
                            </span>
                        ),
                        children: <HomeGuideConfigForm />,
                    },
                ]}
            />
        </Card>
    );
}
